// tailwind entry
import "./index.css";
import "@vendistri/ui/styles.css";


import reportWebVitals from "./reportWebVitals";
import { useUserStore } from "./user/state/store";
import { selector as userSelector } from "./user/state";
import { replaceBrowserSession } from "./user/state/store";
import { readBrowserSessionIdentity } from "./user/api";
import { SessionBoundary } from "@vendistri/ui";
import {
  accountRouteRedirect,
  isContactRole,
  isContactOnlyUser,
  isLeadOnlyAllowedPath,
  isOperatorAllowedPath,
  isOperatorUser,
  operatorHomePath,
  isOrglessUser,
} from "./vend/access";
import { useThemeMode } from "./theme";
import { secureMarketplaceUrl } from "./marketplaceUrl";
import { ThemeContext } from "./themeContext";
import { useEffectiveVendUser } from "./vend/effectiveUser";
import {
  isSuperuserSupportAccountPendingState,
  useSuperuserContextStore,
} from "./vend/state/superuserContext";
import { startAppVersionMonitor } from "./appVersion";
import { AppErrorBoundary } from "./AppErrorBoundary";
import { AppScrollRestoration } from "./AppScrollRestoration";
import { loadRoutePlanner } from "./vend/navigation/pagePreload";
// Keep the sign-in screen ready so signing out never waits on a route chunk.
import SigninPage from "./user/signin";

// third party
import ReactDOM from "react-dom/client";
import React, { useEffect, useRef } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  Outlet,
  useParams,
  useLocation,
} from "react-router-dom";
import { useShallow } from "zustand/shallow";

let dashboardModule: Promise<typeof import("./vend/dashboard")> | undefined;
const loadDashboard = () => {
  dashboardModule ??= import("./vend/dashboard");
  return dashboardModule;
};
if (window.location.pathname === "/dashboard") {
  void loadDashboard();
}
const Dashboard = React.lazy(loadDashboard);
const VendShell = React.lazy(() =>
  import("./vend/layout").then((module) => ({ default: module.VendShell })),
);
const ContactShell = React.lazy(() =>
  import("./vend/contactPortal/contactLayout").then((module) => ({
    default: module.ContactShell,
  })),
);
const TaskRealtimeListener = React.lazy(() =>
  import("./vend/realtime/TaskRealtimeListener").then((module) => ({
    default: module.TaskRealtimeListener,
  })),
);
const SuperuserSupportRealtimeListener = React.lazy(() =>
  import("./vend/realtime/SuperuserSupportRealtimeListener").then((module) => ({
    default: module.SuperuserSupportRealtimeListener,
  })),
);
const SuperuserSupportAccountLoader = React.lazy(() =>
  import("./vend/components/SuperuserSupportAccountLoader").then((module) => ({
    default: module.SuperuserSupportAccountLoader,
  })),
);
const AdministrationSettingsPage = React.lazy(() => import("./vend/administration/settings/AdministrationSettingsPage"));
const RequireSuperuser = React.lazy(() =>
  import("./vend/administration/RequireSuperuser").then((module) => ({
    default: module.RequireSuperuser,
  })),
);
const RoutesPage = React.lazy(() => import("./vend/routes"));
const RoutePlannerPage = React.lazy(loadRoutePlanner);
const LocationsPage = React.lazy(() => import("./vend/locations"));
const LocationSchedulingPage = React.lazy(() => import("./vend/locationScheduling"));
const LocationDetailPage = React.lazy(() => import("./vend/locationDetail"));
const MachinesPage = React.lazy(() => import("./vend/machines"));
const MachineDetailPage = React.lazy(() => import("./vend/machineDetail"));
const InventoryPage = React.lazy(() => import("./vend/inventoryPage"));
const ExpensesPage = React.lazy(() => import("./vend/expense/ExpensesPage"));
const ExpenseDetailPage = React.lazy(() => import("./vend/expense/ExpenseDetailPage"));
const ExpenseScheduleDetailPage = React.lazy(() => import("./vend/expense/ExpenseScheduleDetailPage"));
const ExpenseCategoryDetailPage = React.lazy(() => import("./vend/expense/ExpenseCategoryDetailPage"));
const ExpensePaymentAccountDetailPage = React.lazy(() => import("./vend/expense/ExpensePaymentAccountDetailPage"));
const ExpenseProjectDetailPage = React.lazy(() => import("./vend/expense/ExpenseProjectDetailPage"));
const ExpenseMerchantDetailPage = React.lazy(() => import("./vend/expense/ExpenseMerchantDetailPage"));
const TasksPage = React.lazy(() => import("./vend/tasks"));
const TaskDetailPage = React.lazy(() => import("./vend/taskDetail"));
const ReportsPage = React.lazy(() => import("./vend/reports"));
const ConfigurationsPage = React.lazy(() => import("./vend/configurations"));
const ProfilePage = React.lazy(() => import("./vend/profile"));
const AccountSettingsPage = React.lazy(() => import("./vend/accountSettings"));
const StripeApp = React.lazy(() => import("./vend/stripeWrapper"));
const SubscriptionPage = React.lazy(() => import("./vend/subscription"));
const SubscriptionChargeDetailPage = React.lazy(() => import("./vend/subscriptionChargeDetail"));
const HelpCenterPage = React.lazy(() => import("./vend/helpCenter"));
const MarketplacePage = React.lazy(() => import("./vend/marketplace/MarketplacePage"));
const AppointmentTypeDetailPage = React.lazy(() => import("./vend/appointments/AppointmentTypeDetailPage"));
const AppointmentDetailPage = React.lazy(() => import("./vend/appointments/AppointmentDetailPage"));
const CalendarPage = React.lazy(() => import("./vend/appointments/WorkspaceCalendar"));
const InboxPage = React.lazy(() => import("./vend/communications/InboxPage"));
const MailConnectCallback = React.lazy(() => import("./vend/communications/MailConnectCallback"));
const GrowthPage = React.lazy(() => import("./vend/growth/GrowthPage"));
const ContactActivatePage = React.lazy(() => import("./user/contactActivate"));
const ResetPasswordPage = React.lazy(() => import("./user/resetPassword"));
const InviteAcceptPage = React.lazy(() => import("./user/invite"));
const ContactLocationsPage = React.lazy(() => import("./vend/contactPortal/contactLocations"));
const ContactLocationDetailPage = React.lazy(() => import("./vend/contactPortal/contactLocationDetail"));
const ContactHelpCenterPage = React.lazy(() => import("./vend/contactPortal/helpCenter"));
const ServiceFormsPage = React.lazy(() => import("./serviceForms"));
const AdministrationLayout = React.lazy(() =>
  import("./vend/administration/AdministrationLayout").then((module) => ({
    default: module.AdministrationLayout,
  })),
);
const MobileAppsPage = React.lazy(() => import("./vend/administration/mobileApps/MobileAppsPage"));
const AdministrationHelpCenterPage = React.lazy(() => import("./vend/administration/helpCenter/AdministrationHelpCenterPage"));
const AdministrationDashboardPage = React.lazy(() => import("./vend/administration/dashboard/AdministrationDashboardPage"));
const AdministrationOrganizationsPage = React.lazy(() => import("./vend/administration/organizations/AdministrationOrganizationsPage"));
const AdministrationOrganizationDetailPage = React.lazy(() => import("./vend/administration/organizations/AdministrationOrganizationDetailPage"));
const AdministrationOrganizationChargeDetailPage = React.lazy(() => import("./vend/administration/organizations/AdministrationOrganizationChargeDetailPage"));
const BackendHealthPage = React.lazy(() => import("./vend/administration/backendHealth/BackendHealthPage"));
const AuditLogPage = React.lazy(() => import("./vend/administration/audit/AuditLogPage"));
const AdministrationProfilePage = React.lazy(() => import("./vend/administration/account/AdministrationProfilePage"));
const AdministrationAccountSettingsPage = React.lazy(() => import("./vend/administration/account/AdministrationAccountSettingsPage"));
const AdministrationSalesRoutes = React.lazy(() => import("./vend/administration/sales/SalesRoutes"));
const AdministrationMarketplaceRoutes = React.lazy(() => import("./vend/administration/marketplace/MarketplaceRoutes"));

const backendUrl = process.env.REACT_APP_BACKEND_URL;
if (backendUrl) {
  try {
    const backendOrigin = new URL(backendUrl, window.location.origin).origin;
    if (backendOrigin !== window.location.origin) {
      const preconnect = document.createElement("link");
      preconnect.rel = "preconnect";
      preconnect.href = backendOrigin;
      preconnect.crossOrigin = "use-credentials";
      document.head.appendChild(preconnect);
      const dnsPrefetch = document.createElement("link");
      dnsPrefetch.rel = "dns-prefetch";
      dnsPrefetch.href = backendOrigin;
      document.head.appendChild(dnsPrefetch);
    }
  } catch {
    // A malformed optional backend URL should not prevent the app from booting.
  }
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

startAppVersionMonitor();

function Proclaws() {
  const { mode, setMode } = useThemeMode();
  const init = useUserStore((state) => state.init);
  const userId = useUserStore((state) => state.id);
  const initialized = useUserStore((state) => state.initialized);
  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    init().catch(() => {});
  }, [init]);


  return (
    <ThemeContext.Provider value={{ mode, setMode }}>
      <SessionBoundary identity={initialized ? userId || null : undefined} readIdentity={readBrowserSessionIdentity} onChange={replaceBrowserSession}>
      <BrowserRouter key={userId || "signed-out"}>
        <AppScrollRestoration />
        <AuthenticatedRealtimeListeners />
        <React.Suspense fallback={null}>
        <Routes>
          <Route path="/mail-connect" element={<MailConnectCallback />} />
          <Route path={"/stripe"} element={<StripeApp />} />
          <Route path={"/"} element={<SigninPage />} />
          <Route path={"/invite"} element={<InviteAcceptPage />} />
          <Route path={"/reset-password"} element={<ResetPasswordPage />} />
          <Route path={"/service-forms"} element={<ServiceFormsPage />} />
          <Route path={"/contact/activate"} element={<ContactActivatePage />} />
          <Route element={<ProtectedShell />}>
            <Route path={"/dashboard"} element={<ProtectedDashboard />} />
            <Route path={"/:uid"} element={<ProtectedDashboard />} />
            <Route path={"/routes"} element={<RoutesPage />} />
            <Route path={"/route-planner"} element={<RoutePlannerPage />} />
            <Route path={"/locations"} element={<LocationsPage />} />
            <Route path={"/locations/scheduling"} element={<LocationSchedulingPage />} />
            <Route path={"/locations/:id"} element={<LocationDetailPage />} />
            <Route path={"/machines"} element={<MachinesPage />} />
            <Route path={"/machines/:id"} element={<MachineDetailPage />} />
            <Route path={"/inventory"} element={<InventoryPage />} />
            <Route path={"/inventory/:sourceType/:sourceId"} element={<InventoryPage />} />
            <Route path={"/expenses"} element={<ExpensesPage />} />
            <Route
              path={"/expenses/schedules/:scheduleId"}
              element={<ExpenseScheduleDetailPage />}
            />
            <Route
              path={"/expenses/categories/:categoryId"}
              element={<ExpenseCategoryDetailPage />}
            />
            <Route
              path={"/expenses/payment-accounts/:paymentAccountId"}
              element={<ExpensePaymentAccountDetailPage />}
            />
            <Route
              path={"/expenses/projects/:projectId"}
              element={<ExpenseProjectDetailPage />}
            />
            <Route
              path={"/expenses/merchants/:merchantKey"}
              element={<ExpenseMerchantDetailPage />}
            />
            <Route path={"/expenses/:expenseId"} element={<ExpenseDetailPage />} />
            <Route path={"/tasks"} element={<TasksPage />} />
            <Route path={"/tasks/:id"} element={<TaskDetailPage />} />
            <Route path={"/reports"} element={<ReportsPage />} />
            <Route path={"/configurations"} element={<ConfigurationsPage />} />
            <Route path={"/organization"} element={<ProfilePage />} />
            <Route path={"/profile"} element={<ProfilePage />} />
            <Route path={"/account-settings"} element={<AccountSettingsPage />} />
            <Route path={"/subscription"} element={<SubscriptionPage />} />
            <Route
              path={"/subscription/charges/:id"}
              element={<SubscriptionChargeDetailPage />}
            />
            <Route path={"/help-center"} element={<HelpCenterPage />} />
            <Route path={"/help-center/:topic"} element={<HelpCenterPage />} />
            <Route path={"/help-center/:topic/:slug"} element={<HelpCenterPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/inbox/appointment-types/:code" element={<AppointmentTypeDetailPage platform={false} />} />
            <Route path="/inbox/appointments/:id" element={<AppointmentDetailPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path={"/growth/*"} element={<GrowthPage />} />
            <Route path={"/marketplace/*"} element={<MarketplacePage legacyRedirect />} />
          </Route>
          <Route element={<ProtectedContactShell />}>
            <Route path={"/contact/locations"} element={<ContactLocationsPage />} />
            <Route
              path={"/contact/locations/:id"}
              element={<ContactLocationDetailPage />}
            />
            <Route path={"/contact/tasks/:id"} element={<TaskDetailPage />} />
            <Route path={"/contact/help-center"} element={<ContactHelpCenterPage />} />
            <Route
              path={"/contact/account-settings"}
              element={<AccountSettingsPage />}
            />
          </Route>
          <Route element={<RequireSuperuser />}>
            <Route path="/administration" element={<AdministrationLayout />}>
              <Route index element={<AdministrationDashboardPage />} />
              <Route path="organizations" element={<AdministrationOrganizationsPage />} />
              <Route path="organizations/:id/*" element={<AdministrationOrganizationDetailPage />} />
              <Route path="organizations/:id/billing/charges/:chargeId" element={<AdministrationOrganizationChargeDetailPage />} />
              <Route path="mobile-apps" element={<MobileAppsPage />} />
              <Route path="settings" element={<Navigate to="/administration/settings/payments" replace />} />
              <Route path="settings/:tab" element={<AdministrationSettingsPage />} />
              <Route path="backend-health" element={<BackendHealthPage />} />
              <Route path="audit" element={<AuditLogPage />} />
              <Route path="profile" element={<AdministrationProfilePage />} />
              <Route path="account-settings" element={<AdministrationAccountSettingsPage />} />
              <Route
                path="marketplace/*"
                element={<AdministrationMarketplaceRoutes />}
              />
              <Route path="inbox" element={<InboxPage platform />} />
              <Route path="inbox/appointments/:id" element={<AppointmentDetailPage platform />} />
              <Route path="sales/*" element={<AdministrationSalesRoutes />} />
              <Route path="appointment-types" element={<Navigate to="/administration/inbox?view=types" replace />} />
              <Route path="inbox/appointment-types/:code" element={<AppointmentTypeDetailPage />} />
              <Route path="help-center" element={<AdministrationHelpCenterPage />} />
              <Route path="help-center/:topic" element={<AdministrationHelpCenterPage />} />
              <Route path="help-center/:topic/:slug" element={<AdministrationHelpCenterPage />} />
            </Route>
          </Route>
          <Route path="*" element={<SigninPage />} />
        </Routes>
        </React.Suspense>
      </BrowserRouter>
      </SessionBoundary>
    </ThemeContext.Provider>
  );
}

function AuthenticatedRealtimeListeners() {
  const initialized = useUserStore((state) => state.initialized);
  const isAuthenticated = useUserStore((state) => state.isAuthenticated);
  if (!initialized || !isAuthenticated) return null;
  return (
    <React.Suspense fallback={null}>
      <TaskRealtimeListener />
      <SuperuserSupportRealtimeListener />
      <SuperuserSupportAccountLoader />
    </React.Suspense>
  );
}

type RouteUser = ReturnType<typeof useUserStore.getState>;

function isContactExperienceActive(user: RouteUser): boolean {
  return isContactOnlyUser(user) || (user.viewMode === "contact" && isContactRole(user));
}

function getOrgHomePath(user: RouteUser): string {
  if (isOperatorUser(user)) return operatorHomePath(user);
  if (isOrglessUser(user)) return "/account-settings";
  if (user.accountMode === "lead_only") return "/growth/marketplace";
  return "/dashboard";
}

function getOrgRouteRedirect(
  user: RouteUser,
  pathname: string,
): string | null {
  if (!user.isAuthenticated) return "/";
  if (isContactExperienceActive(user)) {
    return pathname.startsWith("/contact") ? null : "/contact/locations";
  }
  if (pathname.startsWith("/contact")) {
    return getOrgHomePath(user);
  }
  if (isOperatorUser(user) && !isOperatorAllowedPath(pathname, user)) {
    return operatorHomePath(user);
  }
  if (isOrglessUser(user) && pathname !== "/account-settings") {
    return "/account-settings";
  }
  if (
    user.accountMode === "lead_only" &&
    !isLeadOnlyAllowedPath(pathname)
  ) {
    return "/growth/marketplace";
  }
  return null;
}

function RouteRedirect({ to }: { to: string }) {
  useEffect(() => {
    if (to === "/growth/marketplace") {
      window.location.replace(secureMarketplaceUrl());
    }
  }, [to]);
  if (to === "/growth/marketplace") return null;
  return <Navigate to={to} replace />;
}

function getContactRouteRedirect(
  user: RouteUser,
): string | null {
  if (!user.isAuthenticated) return "/";
  if (!isContactRole(user)) {
    return getOrgHomePath(user);
  }
  if (!isContactExperienceActive(user)) {
    return getOrgHomePath(user);
  }
  return null;
}

function ProtectedDashboard() {
  const { uid } = useParams();
  const realUser = useUserStore(useShallow(userSelector));
  const user = useEffectiveVendUser();
  const supportAccountPending = useSuperuserContextStore(
    isSuperuserSupportAccountPendingState,
  );

  if (!realUser.initialized) return null;
  if (!supportAccountPending) {
    const redirect = getOrgRouteRedirect(user, "/dashboard");
    if (redirect) {
      return <RouteRedirect to={redirect} />;
    }
  }
  if (uid && uid !== realUser.id) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Dashboard />;
}

function ProtectedShell() {
  const location = useLocation();
  const realUser = useUserStore(useShallow(userSelector));
  const user = useEffectiveVendUser();
  const supportAccountPending = useSuperuserContextStore(
    isSuperuserSupportAccountPendingState,
  );

  if (!realUser.initialized) return null;
  if (!supportAccountPending) {
    const redirect = (user.isAuthenticated && accountRouteRedirect(user, location)) || getOrgRouteRedirect(user, location.pathname);
    if (redirect) {
      return <RouteRedirect to={redirect === "/"
        ? `/?next=${encodeURIComponent(location.pathname + location.search + location.hash)}`
        : redirect} />;
    }
  }

  return (
    <VendShell>
      {supportAccountPending ? <SupportAccountLoadingPage /> : <Outlet />}
    </VendShell>
  );
}

function SupportAccountLoadingPage() {
  return (
    <div className="flex min-h-[16rem] items-center justify-center text-sm text-[rgb(var(--color-muted))]">
      Loading support accounts…
    </div>
  );
}


function ProtectedContactShell() {
  const location = useLocation();
  const realUser = useUserStore(useShallow(userSelector));
  const user = useEffectiveVendUser();
  const supportAccountPending = useSuperuserContextStore(
    isSuperuserSupportAccountPendingState,
  );

  if (!realUser.initialized) return null;
  if (!supportAccountPending) {
    const redirect = (user.isAuthenticated && accountRouteRedirect(user, location)) || getContactRouteRedirect(user);
    if (redirect) {
      return <Navigate to={redirect} replace />;
    }
  }

  return (
    <ContactShell>
      <Outlet />
    </ContactShell>
  );
}

root.render(
  // <React.StrictMode>
  <AppErrorBoundary>
    <Proclaws />
  </AppErrorBoundary>
  // </React.StrictMode>
);

reportWebVitals();
